/**
 * Reduce a Redux slice to a diagnostic fingerprint: how much is loaded, is it
 * still loading, did it error. Never entity payloads — those are large, and
 * volume is not what makes a bug reproducible.
 *
 * @param {Object} [slice] - Any slice, entity-adapter shaped or not
 * @returns {Object|null} `{ count, loading, error }`, or null if absent
 */
function sliceFingerprint(slice) {
  if (!slice) return null

  const count = Array.isArray(slice.ids)
    ? slice.ids.length
    : Array.isArray(slice.items)
      ? slice.items.length
      : undefined

  return {
    count: count ?? null,
    loading: !!slice.loading,
    error: slice.error ?? null,
  }
}

/**
 * Build an allowlisted snapshot of Redux state to attach to a report.
 *
 * This is an allowlist by construction, and must stay one. `state.auth` holds
 * the signed-in user's own security credentials — `mfa.setupData.secret` (a
 * TOTP seed), `mfa.setupData.otpauth_uri` and `mfa.backupCodes` — so it is
 * never spread. A deletion-based approach (`delete summary.auth.mfa`) would
 * re-leak the moment someone adds a field; naming the four wanted keys cannot.
 *
 * Only the slices where bugs actually live are included, at depth 1. Reference
 * data (categories, partition types/values, tags, folders, favorites, campuses,
 * users, report templates, settings, notifications) is high-volume and low
 * diagnostic value, so it is left out.
 *
 * @param {Object} [state] - The full Redux state
 * @returns {Object} Snapshot safe to send off-device
 */
export function buildStateSummary(state) {
  if (!state) return {}

  return {
    auth: {
      isAuthenticated: !!state.auth?.isAuthenticated,
      userId: state.auth?.user?.id ?? null,
      currentTenantId: state.auth?.currentTenantId ?? null,
      tenantCount: state.auth?.tenants?.ids?.length ?? 0,
    },
    dashboards: sliceFingerprint(state.dashboards),
    widgets: sliceFingerprint(state.widgets),
    metrics: sliceFingerprint(state.metrics),
    metricEntries: sliceFingerprint(state.metricEntries),
    reports: sliceFingerprint(state.reports),
    forms: sliceFingerprint(state.forms),
    subscriptions: sliceFingerprint(state.subscriptions),
  }
}
